// One shared state for the shell's bell (lot shell / navigation, 13/10). The notification read / dismissed memory was
// module-private in home.tsx; it moves here UNCHANGED (same fixtures, same mapping, same two callbacks), behind a small
// useSyncExternalStore store so every page's bell reads the same counts. Messages keep their own store (messagerie-state):
// the bell only reads its unread counts. Session-local demo: nothing is sent, a reload returns to the fixtures.
import { useSyncExternalStore } from "react";
import { NOTIFICATIONS } from "./home-fixtures";
import { useMessagerie } from "./messagerie-state";
import { health, isConfigured } from "./health-state";
import type { NotificationItem } from "./adapted/notification-center/notification-center";

/** Id / title / route of the validation notice: the shell maps a tap on it to its route (no navigation API in the centre). */
export const HEALTH_NOTICE = { id: "sante-validee", title: "Ton coach a relu ton questionnaire", href: "#/sante?etat=valide" } as const;
const healthNotice = () => { const h = health.get(); return isConfigured(h) && h.review.status === "valide"
  ? [{ id: HEALTH_NOTICE.id, text: HEALTH_NOTICE.title, when: h.review.reviewedAt ? h.review.reviewedAt.slice(11).replace(":", " h ") : "" }] : []; };

/** Read / dismissed state of the notifications (fixtures NOTIFICATIONS), kept in memory for the page session. */
const notifMemory: Record<string, { read?: boolean; dismissed?: boolean }> = {};
let version = 0;
const listeners = new Set<() => void>();
const changed = () => { version++; listeners.forEach(l => l()); };

export const inbox = {
  subscribe: (l: () => void) => { listeners.add(l); return () => listeners.delete(l); },
  version: () => version,
  // Questionnaire santé (14/10): ONE notice when the coach's review is « valide », only under the explicit configuration (same
  // mapping and read / dismissed memory). The notification centre has no navigation: the notice does not open #/sante.
  notificationItems: (): NotificationItem[] => [...healthNotice(), ...NOTIFICATIONS].filter(n => !notifMemory[n.id]?.dismissed)
    .map(n => ({ id: n.id, title: n.text, time: n.when, read: !!notifMemory[n.id]?.read, tone: "info" })),
  rememberRead: (n: NotificationItem, read: boolean) => { notifMemory[n.id] = { ...notifMemory[n.id], read }; changed(); },
  rememberDismiss: (n: NotificationItem) => { notifMemory[n.id] = { ...notifMemory[n.id], dismissed: true }; changed(); },
};

/** Real counts only: unread notifications (not dismissed) and unread coach messages; the bell shows their sum. */
export function useInboxCounts() {
  useSyncExternalStore(inbox.subscribe, inbox.version, inbox.version);
  useSyncExternalStore(health.subscribe, health.get, health.get);
  const m = useMessagerie();
  const notifications = inbox.notificationItems().filter(n => !n.read).length;
  const messages = Object.values(m.unread).reduce((a, b) => a + b, 0);
  return { notifications, messages, total: notifications + messages };
}
